import { useEffect, useMemo, useRef, useState } from 'react'
import { renderSite, type Site } from '@simplelaunch/renderer'

interface Props {
  readonly site: Site
}

/**
 * Live preview.
 *
 * Renders through the exact function the publisher Lambda uses and drops the
 * result into an iframe via srcdoc. The iframe matters for more than layout
 * isolation: the page carries its own <style> and an inline countdown script,
 * and running that in the builder's own document would leak styles both ways.
 *
 * `sandbox` allows scripts but withholds same-origin, so the preview can run
 * its countdown but cannot reach the builder's storage or DOM.
 */
export function Preview({ site }: Props) {
  // Point asset URLs at whatever origin is serving the builder so fonts and
  // background photos resolve in local dev and in preview deploys, not just
  // against the production domain.
  const assetOrigin = typeof window === 'undefined' ? '' : window.location.origin
  const html = useMemo(() => renderSite(site, { assetOrigin }), [site, assetOrigin])
  const [debounced, setDebounced] = useState(html)
  const first = useRef(true)

  useEffect(() => {
    // Re-parsing a whole document on every keystroke restarts the countdown
    // and makes typing feel laggy, so settle briefly first.
    if (first.current) {
      first.current = false
      setDebounced(html)
      return
    }
    const t = setTimeout(() => setDebounced(html), 180)
    return () => clearTimeout(t)
  }, [html])

  return (
    <div className="frame">
      <iframe
        title="Live preview of your launch page"
        srcDoc={debounced}
        sandbox="allow-scripts"
        loading="eager"
      />
    </div>
  )
}
